/**
 * @fileoverview Controls bar for the Table component.
 * Includes everything inline with pagination, primary action, etc.
 * Does NOT include the passed filterBar or filterPanel.
 *
 * @author Derek Seo (dseo@yext.com)
 */
// @ts-nocheck
import {MenuController} from '/ui/components/menu/menucontroller';

import {DeprecatedOldButton as Button} from '/ui/components/button/button';
import {Checkbox} from '/ui/components/checkbox/checkbox';
import {CursorPaginationControls} from '/ui/components/cursorpagination/cursorpaginationcontrols';
import {Action} from '/ui/components/menu/types/action';
import {PaginationControls} from '/ui/components/pagination/paginationcontrols';
import {ColumnPicker} from '/ui/components/table/controls/columnpicker';
import * as styles from '/ui/components/table/controls/tablecontrols.module.scss';
import * as Types from '/ui/components/table/types';

/**
 * Returns an object containing the top controls bar and
 * the bottom controls bar.
 *
 * @param {!Types.TableProps} props
 * @return {{
 *   topControls: React.ReactNode,
 *   bottomControls: React.ReactNode,
 * }}
 */
export function tableControls({
  columns,
  pagination,
  paginationBelow,
  primaryActionOnClick,
  primaryActionLabel,
  moreActions,
  rowSelectionOptions,
  isLoading,
  columnPickerConfig,
}) {
  const controls = {
    topControls: null,
    bottomControls: null,
  };

  const topControlContents = ({
    left: /** @type {!Array<React.ReactElement>} */ ([]),
    right: /** @type {!Array<React.ReactElement>} */ ([]),
  });
  const bottomControlContents = /** @type {!Array<React.ReactElement>} */ ([]);
  if (rowSelectionOptions && rowSelectionOptions.selectActions) {
    const {selectActions, numSelected, selectAllState} = rowSelectionOptions;

    topControlContents.left.push(<MenuController
      key="row-select"
      disabled={isLoading}
      allowContainerOverflow={true}
      actions={/** @type {!Array<Action>} */(selectActions)}
      label={
        /** @type {!React.ReactNode} */
        (
          <Checkbox
            indeterminate={selectAllState === Types.CheckboxState.INDETERMINATE}
            checked={selectAllState === Types.CheckboxState.SELECTED}
            label={
              numSelected === 0 ? '' : (`(${numSelected === null ? 'Many' : numSelected})`)
            }
            readOnly
            centerContents />
        )
      }
    />);
  }
  primaryActionOnClick && topControlContents.left.push(
    <Button key="primary-action" onClick={primaryActionOnClick} disabled={isLoading}>
      { primaryActionLabel }
    </Button>,
  );
  if (moreActions) {
    // TODO (bteo): Remove @suppress annotation when better React typings are available (UX-1938)
    /** @suppress {missingProperties} */
    const moreActionsInvalid = moreActions && moreActions.type !== MenuController;
    if (moreActionsInvalid) {
      throw new Error('<Table/>: moreActions should be a <MenuController/> component.');
    }
    topControlContents.left.push(<React.Fragment key="more-actions">{moreActions}</React.Fragment>);
  }

  columnPickerConfig && topControlContents.right.push(<ColumnPicker
    key="column-picker"
    columns={columns}
    isLoading={isLoading}
    columnPickerConfig={columnPickerConfig}
  />);
  if (pagination) {
    // TODO (bteo): Remove @suppress annotation when better React typings are available (UX-1938)
    /** @suppress {missingProperties} */
    const paginationInvalid = pagination && ![PaginationControls, CursorPaginationControls].includes(pagination.type);
    if (paginationInvalid) {
      throw new Error('<Table/>: pagination should be a <PaginationControls/> or <CursorPaginationControls/> component.');
    }
    topControlContents.right.push(<React.Fragment key="pagination">{pagination}</React.Fragment>);
    if (paginationBelow) {
      bottomControlContents.push(<React.Fragment key="pagination">{pagination}</React.Fragment>);
    }
  }

  if (topControlContents.left.length !== 0 || topControlContents.right.length !== 0) {
    controls.topControls = (
      <div className={styles.container}>
        <div className={styles.leftControls}>
          {topControlContents.left}
        </div>
        <div className={styles.rightControls}>
          {topControlContents.right}
        </div>
      </div>
    );
  }

  if (bottomControlContents.length !== 0) {
    controls.bottomControls = (
      <div className={styles.bottomControls}>
        {pagination}
      </div>
    );
  }
  return controls;
}
